import { useState, useEffect, useRef } from 'react'
import { Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react'
import { AnimalVideoUpload, AnimalFotoUpload } from './AnimalMidiaUpload'
import { lotesService } from '../../services/lotesService'

const novoAnimal = () => ({
  nome: '', sexo: '', registro: '', data_nascimento: '', pelagem: '', peso: '', percentual_venda: 100,
  raca_id: '', tipo_animal_id: null, pai: '', pai_registro: '', mae: '', mae_registro: '',
  genealogia_avos: {}, localizacao: '', informacoes: '',
  foto_url: '', video_url: '', video_poster_url: '', link_video: '',
  video_pending_key: null, poster_pending_key: null, foto_pending_key: null,
  animal_cadastro_id: null,
})

// Campos do cadastro mestre que pré-preenchem o animal do lote ao selecionar um
// resultado da busca (mesma lista "de identidade" usada no backend, exceto os
// específicos do vínculo lote↔animal: percentual_venda, ordem).
const CAMPOS_ANIMAL_CADASTRO = [
  'nome', 'sexo', 'registro', 'pelagem', 'peso',
  'pai', 'pai_registro', 'mae', 'mae_registro', 'genealogia_avos',
  'localizacao', 'raca_id', 'tipo_animal_id', 'informacoes',
  'foto_url', 'video_url', 'video_poster_url', 'link_video',
]

// Sugere o tipo (Potro/Potra/Cavalo/Égua) a partir de sexo + idade calculada da
// data de nascimento — só preenche automaticamente enquanto o campo estiver
// vazio, nunca sobrescreve uma escolha manual do usuário.
function normalizarTexto(s) {
  return (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

function sugerirTipoAnimal(sexo, dataNascimento, tiposAnimal) {
  if (!tiposAnimal?.length) return null
  let jovem = false
  if (dataNascimento) {
    const nasc = new Date(dataNascimento)
    if (!Number.isNaN(nasc.getTime())) {
      const meses = (Date.now() - nasc.getTime()) / (1000 * 60 * 60 * 24 * 30.44)
      jovem = meses < 24
    }
  }
  const nomeAlvo = sexo === 'F' ? (jovem ? 'potra' : 'egua') : (jovem ? 'potro' : 'cavalo')
  const encontrado = tiposAnimal.find(t => normalizarTexto(t.nome).includes(nomeAlvo))
  return encontrado ? encontrado.id : null
}

// ── Combobox de Nome do animal: busca com debounce no cadastro mestre reutilizável
// (mesmo padrão visual/comportamental do RacaCombobox abaixo) — ao selecionar um
// resultado, pré-preenche todos os campos do animal de uma vez.
function AnimalNomeCombobox({ nome, organizadorId, onChangeNome, onSelectCadastro }) {
  const [open, setOpen] = useState(false)
  const [resultados, setResultados] = useState([])
  const [buscando, setBuscando] = useState(false)
  const debounceRef = useRef(null)

  const handleInputChange = (v) => {
    onChangeNome(v)
    setOpen(true)
    clearTimeout(debounceRef.current)
    if (!organizadorId || v.trim().length < 2) {
      setResultados([])
      return
    }
    debounceRef.current = setTimeout(async () => {
      setBuscando(true)
      try {
        const data = await lotesService.buscarAnimaisCadastro(organizadorId, v.trim())
        setResultados(data || [])
      } catch {
        setResultados([])
      } finally {
        setBuscando(false)
      }
    }, 300)
  }

  const termoAtual = (nome || '').trim()

  return (
    <div className="relative">
      <input
        type="text" required
        value={nome || ''}
        onChange={(e) => handleInputChange(e.target.value.toUpperCase())}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="input-rematei text-sm"
        placeholder="Ex: Trovão da Serra"
      />
      {open && termoAtual.length >= 2 && (
        <ul className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {buscando && <li className="px-3 py-1.5 text-xs text-gray-400">Buscando...</li>}
          {!buscando && resultados.map(a => (
            <li
              key={a.id}
              onMouseDown={(e) => { e.preventDefault(); onSelectCadastro(a); setOpen(false) }}
              className="px-3 py-1.5 text-sm hover:bg-blue-50 cursor-pointer"
            >
              {a.nome}
            </li>
          ))}
          {!buscando && resultados.length === 0 && (
            <li className="px-3 py-1.5 text-xs text-gray-400">Nenhum animal encontrado — será cadastrado como novo ao salvar</li>
          )}
        </ul>
      )}
    </div>
  )
}

// ── Estrutura da árvore de genealogia (3 gerações: pais → avós → bisavós) ──────
// Grid fixo de 3 colunas × 4 linhas por linhagem — cada geração ocupa exatamente
// 1fr, então as caixas ficam com largura consistente (não distorce indo pros
// bisavós, ao contrário do layout recursivo anterior).
const NOS_PATERNA = [
  { key: 'pai', label: 'Pai', col: 1, rowStart: 1, rowSpan: 4, parent: null },
  { key: 'avoPaiPai', label: 'Avô Paterno', col: 2, rowStart: 1, rowSpan: 2, parent: 'pai' },
  { key: 'avoPaiMae', label: 'Avó Paterna', col: 2, rowStart: 3, rowSpan: 2, parent: 'pai' },
  { key: 'bisavoPaiPaiPai', label: 'Bisavô', col: 3, rowStart: 1, rowSpan: 1, parent: 'avoPaiPai' },
  { key: 'bisavoPaiPaiMae', label: 'Bisavó', col: 3, rowStart: 2, rowSpan: 1, parent: 'avoPaiPai' },
  { key: 'bisavoPaiMaePai', label: 'Bisavô', col: 3, rowStart: 3, rowSpan: 1, parent: 'avoPaiMae' },
  { key: 'bisavoPaiMaeMae', label: 'Bisavó', col: 3, rowStart: 4, rowSpan: 1, parent: 'avoPaiMae' },
]
const NOS_MATERNA = [
  { key: 'mae', label: 'Mãe', col: 1, rowStart: 1, rowSpan: 4, parent: null },
  { key: 'avoMaePai', label: 'Avô Materno', col: 2, rowStart: 1, rowSpan: 2, parent: 'mae' },
  { key: 'avoMaeMae', label: 'Avó Materna', col: 2, rowStart: 3, rowSpan: 2, parent: 'mae' },
  { key: 'bisavoMaePaiPai', label: 'Bisavô', col: 3, rowStart: 1, rowSpan: 1, parent: 'avoMaePai' },
  { key: 'bisavoMaePaiMae', label: 'Bisavó', col: 3, rowStart: 2, rowSpan: 1, parent: 'avoMaePai' },
  { key: 'bisavoMaeMaePai', label: 'Bisavô', col: 3, rowStart: 3, rowSpan: 1, parent: 'avoMaeMae' },
  { key: 'bisavoMaeMaeMae', label: 'Bisavó', col: 3, rowStart: 4, rowSpan: 1, parent: 'avoMaeMae' },
]
const RAIZ_KEYS = new Set(['pai', 'mae'])

// Sexo do ancestral pela própria chave: tudo que termina em "Pai" (ou é 'pai') é
// macho, tudo que termina em "Mae" (ou é 'mae') é fêmea — a posição na árvore já
// carrega essa informação, sem precisar de campo extra.
function isAncestralMacho(key) {
  return key === 'pai' || key.endsWith('Pai')
}

function CaixaAncestral({ node, animal, onChangeCampo }) {
  const isRaiz = RAIZ_KEYS.has(node.key)
  const nome = isRaiz ? (animal[node.key] || '') : (animal.genealogia_avos?.[node.key]?.nome || '')
  const registro = isRaiz ? (animal[`${node.key}_registro`] || '') : (animal.genealogia_avos?.[node.key]?.registro || '')
  const macho = isAncestralMacho(node.key)
  const boxClasses = macho
    ? 'bg-blue-50 border-blue-200 focus:ring-blue-400 focus:border-blue-400'
    : 'bg-pink-50 border-pink-200 focus:ring-pink-400 focus:border-pink-400'

  return (
    <div
      className="flex flex-col justify-center min-w-0 relative z-10"
      style={{ gridColumn: node.col, gridRow: `${node.rowStart} / span ${node.rowSpan}` }}
    >
      <span className={`font-medium text-gray-400 mb-0.5 truncate ${isRaiz ? 'text-xs' : 'text-[10px]'}`}>{node.label}</span>
      <div className={`rounded-lg border overflow-hidden ${boxClasses} ${isRaiz ? 'border-2 shadow-sm' : ''}`}>
        <input
          type="text" value={nome}
          onChange={(e) => onChangeCampo(node.key, 'nome', e.target.value.toUpperCase())}
          className={`w-full bg-transparent focus:outline-none text-gray-800 ${
            isRaiz ? 'px-3 py-3 text-lg font-bold' : 'px-3 py-2 text-sm font-medium'
          }`}
          placeholder="Nome"
        />
        <input
          type="text" value={registro}
          onChange={(e) => onChangeCampo(node.key, 'registro', e.target.value.toUpperCase())}
          className="w-full bg-black/5 px-3 py-1.5 text-xs text-gray-600 focus:outline-none border-t border-black/10"
          placeholder="Registro"
        />
      </div>
    </div>
  )
}

// Centro vertical (%) e borda esquerda (%) de um nó, dentro do grid de 3 colunas
// × 4 linhas — usado só pra posicionar as "espinhas" conectoras (aproximação,
// ignora a largura do gap entre colunas, imperceptível visualmente).
const centroY = (node) => ((node.rowStart - 1) + node.rowSpan / 2) / 4 * 100
const bordaEsquerda = (node) => (node.col - 1) / 3 * 100

// Uma barra vertical simples (não SVG — evita distorção de escala) por grupo de
// irmãos, ligando o centro do primeiro ao centro do último filho, na borda
// esquerda da coluna deles.
function calcularEspinhas(nos) {
  const grupos = {}
  nos.forEach(n => {
    if (!n.parent) return
    if (!grupos[n.parent]) grupos[n.parent] = []
    grupos[n.parent].push(n)
  })
  return Object.entries(grupos).map(([paiKey, filhos]) => {
    const ys = filhos.map(centroY)
    return {
      key: paiKey,
      x: bordaEsquerda(filhos[0]),
      top: Math.min(...ys),
      bottom: Math.max(...ys),
    }
  })
}

function GenealogiaGrid({ nos, animal, onChangeCampo }) {
  const espinhas = calcularEspinhas(nos)

  return (
    <div className="relative">
      {espinhas.map(e => (
        <div
          key={e.key}
          className="absolute bg-gray-300"
          style={{ left: `${e.x}%`, top: `${e.top}%`, height: `${e.bottom - e.top}%`, width: '2px' }}
        />
      ))}
      <div
        className="relative grid gap-3"
        style={{ gridTemplateColumns: 'repeat(3, 1fr)', gridTemplateRows: 'repeat(4, minmax(0, 1fr))' }}
      >
        {nos.map(node => (
          <CaixaAncestral key={node.key} node={node} animal={animal} onChangeCampo={onChangeCampo} />
        ))}
      </div>
    </div>
  )
}

// ── Combobox de Raça: busca com debounce + opção de criar nova ─────────────────
function RacaCombobox({ value, racas, onSelect, onCreateRaca }) {
  const [search, setSearch] = useState('')
  const [termoDebounced, setTermoDebounced] = useState('')
  const [open, setOpen] = useState(false)
  const [criando, setCriando] = useState(false)
  const debounceRef = useRef(null)

  useEffect(() => {
    const racaAtual = racas.find(r => String(r.id) === String(value))
    setSearch(racaAtual ? racaAtual.nome : '')
  }, [value, racas])

  const handleInputChange = (v) => {
    setSearch(v)
    setOpen(true)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setTermoDebounced(v), 250)
  }

  const filtradas = termoDebounced.trim()
    ? racas.filter(r => r.nome.toLowerCase().includes(termoDebounced.trim().toLowerCase()))
    : racas

  const existeExata = racas.some(r => r.nome.toLowerCase() === search.trim().toLowerCase())

  const handleCriar = async () => {
    if (!search.trim() || criando) return
    setCriando(true)
    try {
      const nova = await onCreateRaca(search.trim())
      if (nova) { onSelect(nova); setOpen(false) }
    } finally {
      setCriando(false)
    }
  }

  return (
    <div className="relative">
      <input
        type="text" value={search}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="input-rematei text-sm" placeholder="Buscar raça..."
      />
      {open && (
        <ul className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filtradas.map(r => (
            <li
              key={r.id}
              onMouseDown={(e) => { e.preventDefault(); onSelect(r); setOpen(false) }}
              className="px-3 py-1.5 text-sm hover:bg-blue-50 cursor-pointer"
            >
              {r.nome}
            </li>
          ))}
          {filtradas.length === 0 && (
            <li className="px-3 py-1.5 text-xs text-gray-400">Nenhuma raça encontrada</li>
          )}
          {search.trim() && !existeExata && (
            <li
              onMouseDown={(e) => { e.preventDefault(); handleCriar() }}
              className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 cursor-pointer font-medium border-t border-gray-100"
            >
              {criando ? 'Criando...' : `+ Criar raça "${search.trim()}"`}
            </li>
          )}
        </ul>
      )}
    </div>
  )
}

// ── Combobox de Tipo (Potro/Potra/Cavalo/Égua): busca com debounce + opção de
// criar novo — mesmo padrão do RacaCombobox acima.
function TipoAnimalCombobox({ value, tiposAnimal, onSelect, onCreateTipo }) {
  const [search, setSearch] = useState('')
  const [termoDebounced, setTermoDebounced] = useState('')
  const [open, setOpen] = useState(false)
  const [criando, setCriando] = useState(false)
  const debounceRef = useRef(null)

  useEffect(() => {
    const tipoAtual = tiposAnimal.find(t => String(t.id) === String(value))
    setSearch(tipoAtual ? tipoAtual.nome : '')
  }, [value, tiposAnimal])

  const handleInputChange = (v) => {
    setSearch(v)
    setOpen(true)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setTermoDebounced(v), 250)
  }

  const filtrados = termoDebounced.trim()
    ? tiposAnimal.filter(t => t.nome.toLowerCase().includes(termoDebounced.trim().toLowerCase()))
    : tiposAnimal

  const existeExato = tiposAnimal.some(t => t.nome.toLowerCase() === search.trim().toLowerCase())

  const handleCriar = async () => {
    if (!search.trim() || criando) return
    setCriando(true)
    try {
      const novo = await onCreateTipo(search.trim())
      if (novo) { onSelect(novo); setOpen(false) }
    } finally {
      setCriando(false)
    }
  }

  return (
    <div className="relative">
      <input
        type="text" required value={search}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="input-rematei text-sm" placeholder="Buscar tipo..."
      />
      {open && (
        <ul className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filtrados.map(t => (
            <li
              key={t.id}
              onMouseDown={(e) => { e.preventDefault(); onSelect(t); setOpen(false) }}
              className="px-3 py-1.5 text-sm hover:bg-blue-50 cursor-pointer"
            >
              {t.nome}
            </li>
          ))}
          {filtrados.length === 0 && (
            <li className="px-3 py-1.5 text-xs text-gray-400">Nenhum tipo encontrado</li>
          )}
          {search.trim() && !existeExato && (
            <li
              onMouseDown={(e) => { e.preventDefault(); handleCriar() }}
              className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 cursor-pointer font-medium border-t border-gray-100"
            >
              {criando ? 'Criando...' : `+ Criar tipo "${search.trim()}"`}
            </li>
          )}
        </ul>
      )}
    </div>
  )
}

// Calcula idade em meses/anos a partir da data de nascimento (mesma lógica da
// sugestão automática de tipo), só pra exibição.
function formatarIdade(dataNascimento) {
  if (!dataNascimento) return null
  const nasc = new Date(dataNascimento)
  if (Number.isNaN(nasc.getTime())) return null
  const meses = Math.floor((Date.now() - nasc.getTime()) / (1000 * 60 * 60 * 24 * 30.44))
  if (meses < 0) return null
  if (meses < 24) return `${meses} ${meses === 1 ? 'mês' : 'meses'}`
  const anos = Math.floor(meses / 12)
  return `${anos} ${anos === 1 ? 'ano' : 'anos'}`
}

/**
 * Lista editável de animais de um lote (fluxo cavalo).
 * Controlado: recebe `animais` e notifica mudanças via `onChange`.
 */
export default function AnimaisCavaloEditor({ animais, onChange, racas = [], onCreateRaca, tiposAnimal = [], onCreateTipo, organizadorId, leilaoId, disabled }) {
  const [genealogiaAberta, setGenealogiaAberta] = useState({})

  const adicionar = () => {
    onChange([...animais, novoAnimal()])
  }

  const remover = (index) => {
    onChange(animais.filter((_, i) => i !== index))
  }

  const atualizar = (index, campo, valor) => {
    onChange(animais.map((a, i) => {
      if (i !== index) return a
      const atualizado = { ...a, [campo]: valor }
      // Sugere o tipo automaticamente por sexo+idade, só enquanto o campo
      // estiver vazio — nunca sobrescreve uma escolha manual do usuário.
      if ((campo === 'sexo' || campo === 'data_nascimento') && !atualizado.tipo_animal_id) {
        const sugestao = sugerirTipoAnimal(atualizado.sexo, atualizado.data_nascimento, tiposAnimal)
        if (sugestao) atualizado.tipo_animal_id = sugestao
      }
      return atualizado
    }))
  }

  // Ao selecionar um resultado da busca do cadastro mestre: pré-preenche todos os
  // campos do animal de uma vez (patch único) e marca animal_cadastro_id — fica
  // sincronizado com o cadastro (editar aqui depois também atualiza o cadastro,
  // ver substituir_animais no backend).
  const aplicarAnimalCadastro = (index, animalCadastro) => {
    const patch = { animal_cadastro_id: animalCadastro.id }
    CAMPOS_ANIMAL_CADASTRO.forEach(campo => {
      patch[campo] = animalCadastro[campo] ?? ''
    })
    patch.data_nascimento = animalCadastro.data_nascimento ? animalCadastro.data_nascimento.slice(0, 10) : ''
    onChange(animais.map((a, i) => i === index ? { ...a, ...patch } : a))
  }

  // Ancestral raiz (pai/mãe): campo 'nome' vai pra a.pai/a.mae, campo 'registro' vai
  // pra a.pai_registro/a.mae_registro. Demais ancestrais (avós/bisavós): objeto
  // {nome, registro} dentro de genealogia_avos[chave].
  const atualizarAncestral = (index, chave, campo, valor) => {
    if (RAIZ_KEYS.has(chave)) {
      const dbField = campo === 'nome' ? chave : `${chave}_registro`
      atualizar(index, dbField, valor)
      return
    }
    onChange(animais.map((a, i) => i === index
      ? { ...a, genealogia_avos: {
          ...(a.genealogia_avos || {}),
          [chave]: { ...(a.genealogia_avos?.[chave] || {}), [campo]: valor },
        } }
      : a
    ))
  }

  const toggleGenealogia = (index) => {
    setGenealogiaAberta(prev => ({ ...prev, [index]: !prev[index] }))
  }

  return (
    <div className="space-y-4">
      {animais.length === 0 && (
        <p className="text-sm text-gray-500">Nenhum animal adicionado ainda.</p>
      )}

      {animais.map((animal, index) => (
        <fieldset key={index} disabled={disabled} className={`border border-gray-200 rounded-xl p-5 relative bg-gray-50/50 ${disabled ? 'opacity-60' : ''}`}>
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold text-gray-700">
              Animal {index + 1}{animal.nome ? ` — ${animal.nome}` : ''}
            </h4>
            {animais.length > 1 && !disabled && (
              <button
                type="button"
                onClick={() => remover(index)}
                className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                title="Remover animal"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Dados principais */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">% de Venda</label>
              <input
                type="number" min="0" max="100" step="0.01"
                value={animal.percentual_venda}
                onChange={(e) => atualizar(index, 'percentual_venda', e.target.value)}
                className="input-rematei text-sm"
                placeholder="100"
              />
            </div>

            <div className="sm:col-span-2 lg:col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">Nome *</label>
              <AnimalNomeCombobox
                nome={animal.nome}
                organizadorId={organizadorId}
                onChangeNome={(v) => atualizar(index, 'nome', v)}
                onSelectCadastro={(cadastro) => aplicarAnimalCadastro(index, cadastro)}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Sexo *</label>
              <select
                required
                value={animal.sexo || ''}
                onChange={(e) => atualizar(index, 'sexo', e.target.value)}
                className="input-rematei text-sm"
              >
                <option value="">Selecione...</option>
                <option value="M">Macho</option>
                <option value="F">Fêmea</option>
                <option value="C">Castrado</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Registro</label>
              <input
                type="text"
                value={animal.registro || ''}
                onChange={(e) => atualizar(index, 'registro', e.target.value.toUpperCase())}
                className="input-rematei text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Nascimento{formatarIdade(animal.data_nascimento) && (
                  <span className="text-gray-400 font-normal"> — {formatarIdade(animal.data_nascimento)}</span>
                )}
              </label>
              <input
                type="date"
                value={animal.data_nascimento || ''}
                onChange={(e) => atualizar(index, 'data_nascimento', e.target.value)}
                className="input-rematei text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Tipo *</label>
              <TipoAnimalCombobox
                value={animal.tipo_animal_id}
                tiposAnimal={tiposAnimal}
                onSelect={(tipo) => atualizar(index, 'tipo_animal_id', tipo.id)}
                onCreateTipo={onCreateTipo}
              />
            </div>

            <div className="sm:col-span-2 lg:col-span-1">
              <label className="block text-xs font-medium text-gray-500 mb-1">Raça</label>
              <RacaCombobox
                value={animal.raca_id}
                racas={racas}
                onSelect={(raca) => atualizar(index, 'raca_id', raca.id)}
                onCreateRaca={onCreateRaca}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Pelagem</label>
              <input
                type="text"
                value={animal.pelagem || ''}
                onChange={(e) => atualizar(index, 'pelagem', e.target.value.toUpperCase())}
                className="input-rematei text-sm"
                placeholder="Ex: Castanho"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Peso (kg)</label>
              <input
                type="number"
                value={animal.peso ?? ''}
                onChange={(e) => atualizar(index, 'peso', e.target.value)}
                className="input-rematei text-sm"
              />
            </div>
          </div>

          {/* Genealogia */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => toggleGenealogia(index)}
              className="flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-blue-600"
            >
              {genealogiaAberta[index] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              Genealogia (pai, mãe, avós e bisavós)
            </button>

            {genealogiaAberta[index] && (
              <div className="mt-3 space-y-4 overflow-x-auto">
                <div>
                  <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-2">Linha Paterna</p>
                  <GenealogiaGrid
                    nos={NOS_PATERNA}
                    animal={animal}
                    onChangeCampo={(chave, campo, valor) => atualizarAncestral(index, chave, campo, valor)}
                  />
                </div>

                <div className="border-t-2 border-gray-200" />

                <div>
                  <p className="text-xs font-semibold text-pink-600 uppercase tracking-wide mb-2">Linha Materna</p>
                  <GenealogiaGrid
                    nos={NOS_MATERNA}
                    animal={animal}
                    onChangeCampo={(chave, campo, valor) => atualizarAncestral(index, chave, campo, valor)}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Extras */}
          <div className="mt-4 pt-4 border-t border-gray-200 flex flex-col sm:flex-row gap-3">
            <div className="sm:w-[30%]">
              <label className="block text-xs font-medium text-gray-500 mb-1">Localização</label>
              <input
                type="text"
                value={animal.localizacao || ''}
                onChange={(e) => atualizar(index, 'localizacao', e.target.value.toUpperCase())}
                className="input-rematei text-sm"
                placeholder="Ex: Dom Eliseu"
              />
            </div>
            <div className="sm:w-[70%]">
              <label className="block text-xs font-medium text-gray-500 mb-1">Observações</label>
              <textarea
                value={animal.informacoes || ''}
                onChange={(e) => atualizar(index, 'informacoes', e.target.value.toUpperCase())}
                className="input-rematei text-sm" rows="2"
              />
            </div>
          </div>

          {/* Mídia (foto, vídeo e link do YouTube — individual por animal) — 3
              colunas lado a lado no desktop (link/vídeo são mutuamente exclusivos,
              então só um dos dois últimos cards aparece preenchido por vez). */}
          {organizadorId && leilaoId && (
            <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Mídia do Animal</p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <AnimalFotoUpload
                  organizadorId={organizadorId}
                  leilaoId={leilaoId}
                  fotoUrl={animal.foto_url}
                  disabled={disabled}
                  onChange={(patch) => onChange(animais.map((a, i) => i === index ? { ...a, ...patch } : a))}
                />

                {/* Link do YouTube e upload de vídeo são mutuamente exclusivos — só faz
                    sentido usar um ou outro pro mesmo animal. */}
                {!animal.video_url && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Link de Vídeo (YouTube)</label>
                    <input
                      type="text"
                      value={animal.link_video || ''}
                      onChange={(e) => atualizar(index, 'link_video', e.target.value)}
                      className="input-rematei text-sm"
                      placeholder="https://youtube.com/watch?v=..."
                    />
                  </div>
                )}

                {!animal.link_video && (
                  <AnimalVideoUpload
                    organizadorId={organizadorId}
                    leilaoId={leilaoId}
                    videoUrl={animal.video_url}
                    posterUrl={animal.video_poster_url}
                    disabled={disabled}
                    onChange={(patch) => onChange(animais.map((a, i) => i === index ? { ...a, ...patch } : a))}
                  />
                )}
              </div>
            </div>
          )}
        </fieldset>
      ))}

      {!disabled && (
        <button
          type="button"
          onClick={adicionar}
          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          <Plus className="w-4 h-4" /> Adicionar Animal
        </button>
      )}
    </div>
  )
}
